import { api } from '@/api';
import {
  ApiError,
  catchAllErrorHandler,
  ErrorResponse,
  isApiError,
  isSemanticError,
  isServerError,
  isSyntacticError,
  stopErrorPropagation,
} from '@/errorHandling';
import { I18nInfo, I18nKey } from '@/utils/typings';
import { QaEquipmentAddFilterObject } from '@/views/qaEquipment/types';
import { RegisterableQaEquipment } from '@/views/qaEquipment/utils';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';
import { useNotificationStore } from '@/stores/notificationStore';
import { notificationForError } from '@/components/notifications/utils';
import { type NotificationEntry } from '@/components/notifications/types';
import { useToken } from '@/utils/tokens/tokens';

type DetailedError = {
  message: I18nInfo | string;
  response?: ErrorResponse;
  remedyI18n?: I18nInfo;
};

export const useQaEquipmentStore = defineStore('qaEquipment', () => {
  const addConfigurationFilterObject = ref<QaEquipmentAddFilterObject>({});

  const deviceForRegistration = ref<
    (RegisterableQaEquipment & { dpin?: string | null }) | undefined
  >();
  const registrationSuccess = ref(() => {});
  const registrationFailure = ref(() => {});
  const deRegistrationSuccess = ref(() => {});
  const deRegistrationFailure = ref(() => {});
  const registrationPending = ref(false);
  const deRegistrationPending = ref(false);
  const csoPortalFullErrorResponse = ref<ErrorResponse>();
  const detailedError = ref<DetailedError>();
  const csoPortalErrorMessage = computed(() => {
    if (csoPortalFullErrorResponse.value) {
      return `${JSON.stringify(csoPortalFullErrorResponse.value, null, 2)}`;
    }
    return '';
  });

  // =============================== Actions ===================================

  function setDeviceForRegistration(newValue?: RegisterableQaEquipment) {
    deviceForRegistration.value = newValue;
  }
  function setFilterObject(newValue: QaEquipmentAddFilterObject) {
    addConfigurationFilterObject.value = newValue;
  }
  function setCsoPortalFullErrorResponse(newValue?: ErrorResponse) {
    csoPortalFullErrorResponse.value = newValue;
  }

  // ====================== Registration & De-Registration =====================

  function handleRegistrationError(error: Error): ApiError | Error {
    const outerException = (error as any)?.errorResponse;
    const errorType = outerException?.errorType;
    const innerException = outerException?.innerException;

    csoPortalFullErrorResponse.value = innerException;

    const badGatewayRemedies: Record<string, I18nKey> = {
      'Exceptions.CsoPortal.NotFound': 'ExceptionsCsoPortalNotFoundRemedy',
      'Exceptions.CsoPortal.DeviceAlreadyRegisteredToAnotherIpin':
        'ExceptionsCsoPortalDeviceAlreadyRegisteredToAnotherIpinRemedy',
      'Exceptions.CsoPortal.WrongDpin': 'ExceptionsCsoPortalWrongDpinRemedy',
    };

    if (errorType === 'PtwBadGatewayException') {
      const i18n = innerException?.i18n;
      const message = innerException?.message;

      if (i18n) {
        detailedError.value = {
          message: i18n,
          response: outerException,
          remedyI18n: badGatewayRemedies[i18n.key]
            ? { key: badGatewayRemedies[i18n.key] }
            : undefined,
        };
      } else if (message) {
        detailedError.value = { message, response: outerException };
      } else {
        detailedError.value = { message: 'Unknown error', response: outerException };
      }
    } else if (errorType === 'PtwServiceUnavailableException' && outerException?.i18n) {
      detailedError.value = {
        message: outerException.i18n,
        response: outerException,
        remedyI18n:
          outerException.i18n.key === 'Exceptions.CsoPortal.NoConnectionMsg'
            ? { key: 'ExceptionsCsoPortalNoConnectionRemedy' }
            : undefined,
      };
    }

    return error;
  }

  const errorNotification = ref<NotificationEntry>();
  async function catchAllRegistrationErrors(error: Error) {
    await catchAllErrorHandler(error);
    errorNotification.value = useNotificationStore().getMessageEntry(notificationForError(error));
    if (
      errorNotification.value?.message &&
      // do not overwrite the detailed error if it is the same as the one from the error notification
      errorNotification.value.message === detailedError.value?.message
    ) {
      detailedError.value = {
        message: errorNotification.value.message,
      };
    }
    return undefined;
  }

  async function register(registerEntity: RegisterableQaEquipment) {
    if (errorNotification.value?.id) {
      useNotificationStore().removeMessage(errorNotification.value?.id);
      errorNotification.value = undefined;
    }
    detailedError.value = undefined;
    csoPortalFullErrorResponse.value = undefined;
    if (registerEntity.id) {
      const token = useToken(registerEntity);

      token.on.inUse(() => {
        detailedError.value = {
          message: { key: 'EntityIsInEditingState' },
          response: undefined,
        };
      });

      await token.request();

      registrationPending.value = true;
      const { success } = await api.qaEquipmentCloudApi.registerQaEquipment(
        {
          id: registerEntity.id,
        },
        [handleRegistrationError, catchAllRegistrationErrors],
      );

      if (success) {
        registrationSuccess.value?.();
      } else {
        registrationFailure.value?.();
      }
      registrationPending.value = false;
      await token.revoke();
    }
  }
  function handleDeregistrationError(error: Error) {
    const defaultMsg = { key: 'DeRegistrationFailed' } as const;
    detailedError.value = {
      message:
        (isApiError(error) ||
          isServerError(error) ||
          isSemanticError(error) ||
          isSyntacticError(error)) &&
        (error.errorResponse?.i18n || error.errorResponse?.message)
          ? (error.errorResponse.i18n ?? error.errorResponse.message ?? defaultMsg)
          : defaultMsg,
    };
    return error;
  }
  async function deregister(deRegisterEntity: RegisterableQaEquipment) {
    if (deRegisterEntity.id) {
      const token = useToken(deRegisterEntity);

      token.on.inUse(() => {
        detailedError.value = {
          message: {
            key: 'EntityIsInEditingState',
          },
          response: undefined,
        };
      });

      await token.request();

      if (token.isValid.value) {
        deRegistrationPending.value = true;

        const { success } = await api.qaEquipmentCloudApi.deRegisterQaEquipment(
          {
            id: deRegisterEntity.id,
          },
          [handleDeregistrationError, stopErrorPropagation],
        );

        if (success) {
          deRegistrationSuccess.value?.();
        } else {
          deRegistrationFailure.value?.();
        }

        deRegistrationPending.value = false;

        await token.revoke();
      }
    }
  }

  // =============================== Error display =============================
  function resetErrorMessage() {
    detailedError.value = undefined;
    csoPortalFullErrorResponse.value = undefined;
  }

  return {
    addConfigurationFilterObject,
    deviceForRegistration,
    register,
    deregister,
    registrationPending,
    deRegistrationPending,
    detailedError,
    csoPortalErrorMessage,
    resetErrorMessage,
    registrationSuccess,
    registrationFailure,
    deRegistrationSuccess,
    deRegistrationFailure,
    csoPortalFullErrorResponse,
    setDeviceForRegistration,
    setFilterObject,
    setCsoPortalFullErrorResponse,
    handleRegistrationError,
  };
});
